import React, { useState, useEffect } from "react";
import { getStoreSettings, updateStoreSettings } from "../../firebase/firestoreService";

const DEFAULTS = { cardStyle: "standard", listingMode: "collections", productView: "inline", sectionTitleStyle: "bold", eventsEnabled: false };

function Toggle({ label, description, options, value, onChange }) {
  return (
    <div className="store-settings__row">
      <div className="store-settings__row-info">
        <p className="store-settings__row-label">{label}</p>
        {description && <p className="store-settings__row-desc">{description}</p>}
      </div>
      <div className="store-settings__toggle-group">
        {options.map((opt) => (
          <button
            key={opt.value}
            className={`store-settings__toggle-btn ${value === opt.value ? "store-settings__toggle-btn--active" : ""}`}
            onClick={() => onChange(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function StoreSettings() {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [saved,    setSaved]    = useState(false);

  useEffect(() => {
    getStoreSettings().then((s) => { setSettings(s || DEFAULTS); setLoading(false); });
  }, []);

  const set = (key, val) => setSettings((s) => ({ ...s, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    await updateStoreSettings(settings);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  if (loading) return <p className="dash-empty">Loading settings…</p>;

  return (
    <div>
      <div className="dash-main__header">
        <h2 className="dash-main__title">Store Settings</h2>
        <button className="btn-primary" onClick={handleSave} disabled={saving}>
          {saved ? "✓ Saved" : saving ? "Saving…" : "Save Changes"}
        </button>
      </div>

      <div className="store-settings__card">
        <p className="store-settings__card-title">Product Listing</p>

        <Toggle
          label="Listing Mode"
          description="Sections groups products under headers (Apparel, Lights & Projectors, etc.). Collections shows the collection filter bar."
          options={[
            { value: "collections", label: "Collections" },
            { value: "sections",    label: "Sections"    },
          ]}
          value={settings.listingMode}
          onChange={(v) => set("listingMode", v)}
        />

        <Toggle
          label="Card Style"
          description="Compact fits more products on screen — no inline pickers. Standard shows the full card with colour swatches and sizes."
          options={[
            { value: "standard", label: "Standard" },
            { value: "compact",  label: "Compact"  },
          ]}
          value={settings.cardStyle}
          onChange={(v) => set("cardStyle", v)}
        />

        <Toggle
          label="Product Detail"
          description="Inline shows colour & size options directly on the card. Modal opens a popup when the customer taps the product."
          options={[
            { value: "inline", label: "Inline" },
            { value: "modal",  label: "Modal"  },
          ]}
          value={settings.productView}
          onChange={(v) => set("productView", v)}
        />

        <Toggle
          label="Section Title Style"
          description="Only applies in Sections mode. Bold shows a large dark heading. Minimal shows a small uppercase label."
          options={[
            { value: "bold",    label: "Bold"    },
            { value: "minimal", label: "Minimal" },
          ]}
          value={settings.sectionTitleStyle}
          onChange={(v) => set("sectionTitleStyle", v)}
        />
      </div>

      <div className="store-settings__card">
        <p className="store-settings__card-title">Events</p>

        <Toggle
          label="Events Feature"
          description="Off hides the Events button from the navbar for everyone except admins, so you can keep building/testing without customers finding it. On makes it visible to all visitors."
          options={[
            { value: false, label: "Off" },
            { value: true,  label: "On"  },
          ]}
          value={settings.eventsEnabled}
          onChange={(v) => set("eventsEnabled", v)}
        />
      </div>

      <div className="store-settings__hint">
        Changes take effect for all customers immediately after saving.
      </div>
    </div>
  );
}
